import { useEffect, useRef } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import type { TemplateFormSchema } from '../validations/edit-template.validation';

import { TEMPLATE_FORM_FIELD } from '../constants/form-field-paths.constant';
import {
  handleCategoryChange,
  handleEquityPlanChange,
  handleSignatoriesChange,
  handleStepThreeFileChange,
  handleSubCategoryChange,
  type ResetHandlerContext,
} from './template-form-reset-handlers';

type Params = {
  defaultValues: TemplateFormSchema;
  setStep: (step: number) => void;
  setValue: UseFormSetValue<TemplateFormSchema>;
  watch: UseFormWatch<TemplateFormSchema>;
};

export const useTemplateFormResets = ({ defaultValues, setStep, setValue, watch }: Params) => {
  const prevFile = useRef<unknown>(defaultValues.stepThree.file);
  const prevSignatories = useRef(defaultValues.stepTwo.signatories);
  const prevCategory = useRef(defaultValues.stepOne.category);
  const prevSubCategory = useRef(defaultValues.stepOne.subCategory);
  const prevEquityPlan = useRef(defaultValues.stepOne.equityPlan);

  useEffect(() => {
    const ctx: ResetHandlerContext = { defaultValues, setStep, setValue };

    const subscription = watch((value, { name, type }) => {
      if (type !== 'change' || name == null) return;

      switch (name) {
        case TEMPLATE_FORM_FIELD.STEP_ONE_CATEGORY:
          handleCategoryChange(value?.stepOne?.category, prevCategory, ctx);
          break;
        case TEMPLATE_FORM_FIELD.STEP_ONE_EQUITY_PLAN:
          handleEquityPlanChange(value?.stepOne?.equityPlan, prevEquityPlan, ctx);
          break;
        case TEMPLATE_FORM_FIELD.STEP_ONE_SUB_CATEGORY:
          handleSubCategoryChange(value?.stepOne?.subCategory, prevSubCategory, ctx);
          break;
        case TEMPLATE_FORM_FIELD.STEP_THREE_FILE:
          handleStepThreeFileChange(value?.stepThree?.file, prevFile, ctx);
          break;
        case TEMPLATE_FORM_FIELD.STEP_TWO_SIGNATORIES:
          handleSignatoriesChange(
            (value?.stepTwo?.signatories ?? []) as TemplateFormSchema['stepTwo']['signatories'],
            prevSignatories,
            ctx,
          );
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [defaultValues, setStep, setValue, watch]);
};
